import { useState } from 'react';

import { Button, Dropdown, FieldLabel, Modal, showSnackbar, Table, TextInput } from 'fluxo-ui';
import { CheckIcon, EditIcon, PlusIcon, SaveIcon, TimesCircleIcon, TrashIcon } from 'fluxo-ui/icons';

import { USER_ROLES } from '../../common/constants';

import type { Column, ComponentEvent, ListItem } from 'fluxo-ui';

interface User {
    id: string;
    name: string;
    email: string;
    role: string;
    status: 'active' | 'suspended' | 'pending';
    joinedAt: string;
    lastLogin?: string;
}

interface UserManagementProps {
    organizationId?: string;
    users?: User[];
    loading?: boolean;
    onInvite?: (email: string, role: string) => Promise<void>;
    onUpdateRole?: (userId: string, role: string) => Promise<void>;
    onSuspend?: (userId: string) => Promise<void>;
    onActivate?: (userId: string) => Promise<void>;
    onRemove?: (userId: string) => Promise<void>;
}

const UserManagement: React.FC<UserManagementProps> = ({
    users = [],
    loading = false,
    onInvite,
    onUpdateRole,
    onSuspend,
    onActivate,
    onRemove,
}) => {
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showRemoveModal, setShowRemoveModal] = useState<{ show: boolean; user?: User }>({ show: false });
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState(USER_ROLES.ORG_MEMBER);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [inviting, setInviting] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [removing, setRemoving] = useState(false);

    const roleOptions: ListItem[] = [
        { label: 'Organization Admin', value: USER_ROLES.ORG_ADMIN },
        { label: 'Moderator', value: USER_ROLES.ORG_MODERATOR },
        { label: 'Instructor', value: USER_ROLES.ORG_INSTRUCTOR },
        { label: 'Member', value: USER_ROLES.ORG_MEMBER },
    ];

    const getStatusBadge = (status: string) => {
        const styles = {
            active: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
            suspended: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
            pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
        };
        return <span className={`px-2 py-1 text-xs rounded-full font-medium ${styles[status as keyof typeof styles]}`}>{status}</span>;
    };

    const getRoleBadge = (role: string) => {
        const colors: Record<string, string> = {
            [USER_ROLES.ORG_ADMIN]:
                'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/20 dark:text-purple-300 dark:border-purple-800',
            [USER_ROLES.ORG_MODERATOR]:
                'bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-900/20 dark:text-teal-300 dark:border-teal-800',
            [USER_ROLES.ORG_INSTRUCTOR]:
                'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800',
            [USER_ROLES.ORG_MEMBER]: 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600',
        };
        return (
            <span className={`px-2 py-1 text-xs rounded border ${colors[role] || colors[USER_ROLES.ORG_MEMBER]}`}>
                {role.replace('org_', '').replace(/_/g, ' ')}
            </span>
        );
    };

    const handleInvite = async () => {
        if (!inviteEmail.trim()) {
            showSnackbar('Please enter an email address', 'Validation Error', { type: 'error' });
            return;
        }

        setInviting(true);
        try {
            await onInvite?.(inviteEmail, inviteRole);
            showSnackbar('Invitation sent successfully', 'Success', { type: 'success' });
            setShowInviteModal(false);
            setInviteEmail('');
            setInviteRole(USER_ROLES.ORG_MEMBER);
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to send invitation', 'Error', { type: 'error' });
        } finally {
            setInviting(false);
        }
    };

    const handleUpdateRole = async () => {
        if (!editingUser) return;

        setUpdating(true);
        try {
            await onUpdateRole?.(editingUser.id, editingUser.role);
            showSnackbar('User role updated', 'Success', { type: 'success' });
            setShowEditModal(false);
            setEditingUser(null);
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to update role', 'Error', { type: 'error' });
        } finally {
            setUpdating(false);
        }
    };

    const handleSuspend = async (userId: string) => {
        try {
            await onSuspend?.(userId);
            showSnackbar('User suspended', 'Success', { type: 'success' });
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to suspend user', 'Error', { type: 'error' });
        }
    };

    const handleActivate = async (userId: string) => {
        try {
            await onActivate?.(userId);
            showSnackbar('User activated', 'Success', { type: 'success' });
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to activate user', 'Error', { type: 'error' });
        }
    };

    const handleRemove = async () => {
        if (!showRemoveModal.user) return;

        setRemoving(true);
        try {
            await onRemove?.(showRemoveModal.user.id);
            showSnackbar('User removed from organization', 'Success', { type: 'success' });
            setShowRemoveModal({ show: false });
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to remove user', 'Error', { type: 'error' });
        } finally {
            setRemoving(false);
        }
    };

    const columns: Column[] = [
        {
            title: 'User',
            field: 'name',
            sortable: true,
            template: (row: User) => (
                <div>
                    <div className="font-medium text-slate-900 dark:text-white">{row.name}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">{row.email}</div>
                </div>
            ),
        },
        {
            title: 'Role',
            field: 'role',
            sortable: true,
            template: (row: User) => getRoleBadge(row.role),
        },
        {
            title: 'Status',
            field: 'status',
            sortable: true,
            template: (row: User) => getStatusBadge(row.status),
        },
        {
            title: 'Joined',
            field: 'joinedAt',
            sortable: true,
            hideBelow: 'md',
            template: (row: User) => (
                <span className="text-sm text-slate-600 dark:text-slate-400">{new Date(row.joinedAt).toLocaleDateString()}</span>
            ),
        },
        {
            title: 'Last Login',
            field: 'lastLogin',
            sortable: true,
            hideBelow: 'lg',
            template: (row: User) => (
                <span className="text-sm text-slate-600 dark:text-slate-400">
                    {row.lastLogin ? new Date(row.lastLogin).toLocaleString() : 'Never'}
                </span>
            ),
        },
        {
            title: 'Actions',
            field: 'actions',
            template: (row: User) => (
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => {
                            setEditingUser(row);
                            setShowEditModal(true);
                        }}
                        className="p-2 text-slate-600 dark:text-slate-400 hover:text-teal-600 dark:hover:text-teal-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors"
                        title="Edit role"
                    >
                        <EditIcon className="w-4 h-4" />
                    </button>
                    {row.status === 'active' ? (
                        <button
                            onClick={() => handleSuspend(row.id)}
                            className="p-2 text-slate-600 dark:text-slate-400 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors"
                            title="Suspend user"
                        >
                            <TimesCircleIcon className="w-4 h-4" />
                        </button>
                    ) : (
                        <button
                            onClick={() => handleActivate(row.id)}
                            className="p-2 text-slate-600 dark:text-slate-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors"
                            title="Activate user"
                        >
                            <CheckIcon className="w-4 h-4" />
                        </button>
                    )}
                    <button
                        onClick={() => setShowRemoveModal({ show: true, user: row })}
                        className="p-2 text-slate-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors"
                        title="Remove user"
                    >
                        <TrashIcon className="w-4 h-4" />
                    </button>
                </div>
            ),
        },
    ];

    const stats = {
        total: users.length,
        active: users.filter((u) => u.status === 'active').length,
        suspended: users.filter((u) => u.status === 'suspended').length,
        pending: users.filter((u) => u.status === 'pending').length,
    };

    return (
        <>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white">User Management</h2>
                        <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Manage organization members and their roles</p>
                    </div>
                    <Button label="Invite User" leftIcon={PlusIcon} variant="primary" onClick={() => setShowInviteModal(true)} />
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                        <div className="text-2xl font-bold text-slate-900 dark:text-white">{stats.total}</div>
                        <div className="text-xs text-slate-600 dark:text-slate-400">Total Users</div>
                    </div>
                    <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                        <div className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.active}</div>
                        <div className="text-xs text-green-600 dark:text-green-400">Active</div>
                    </div>
                    <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                        <div className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.suspended}</div>
                        <div className="text-xs text-red-600 dark:text-red-400">Suspended</div>
                    </div>
                    <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                        <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{stats.pending}</div>
                        <div className="text-xs text-yellow-600 dark:text-yellow-400">Pending</div>
                    </div>
                </div>

                {/* Users Table */}
                <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                    <Table
                        columns={columns}
                        rows={users}
                        totalRows={users.length}
                        isLoading={loading}
                        pagination
                        rowsPerPage={20}
                        noRowsMessage="No users found. Invite users to get started."
                    />
                </div>
            </div>

            {/* Invite User Modal */}
            <Modal isOpen={showInviteModal} onClose={() => !inviting && setShowInviteModal(false)} title="Invite User" size="md">
                <div className="space-y-4">
                    <FieldLabel label="Email Address" required>
                        <TextInput
                            type="email"
                            value={inviteEmail}
                            onChange={(e: ComponentEvent<string>) => setInviteEmail(e.value)}
                            placeholder="user@example.com"
                            disabled={inviting}
                        />
                    </FieldLabel>

                    <FieldLabel label="Role" required>
                        <Dropdown
                            options={roleOptions}
                            value={inviteRole}
                            onChange={(e: ComponentEvent<string>) => setInviteRole(e.value as typeof USER_ROLES.ORG_MEMBER)}
                            disabled={inviting}
                        />
                    </FieldLabel>

                    <div className="flex justify-end gap-3">
                        <Button
                            label="Cancel"
                            variant="default"
                            layout="outlined"
                            onClick={() => setShowInviteModal(false)}
                            disabled={inviting}
                        />
                        <Button
                            label="Send Invitation"
                            leftIcon={PlusIcon}
                            variant="primary"
                            onClick={handleInvite}
                            isLoading={inviting}
                            disabled={inviting}
                        />
                    </div>
                </div>
            </Modal>

            {/* Edit Role Modal */}
            <Modal isOpen={showEditModal} onClose={() => !updating && setShowEditModal(false)} title="Edit User Role" size="md">
                {editingUser && (
                    <div className="space-y-4">
                        <div className="p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
                            <div className="font-medium text-slate-900 dark:text-white">{editingUser.name}</div>
                            <div className="text-sm text-slate-500 dark:text-slate-400">{editingUser.email}</div>
                        </div>

                        <FieldLabel label="Role" required>
                            <Dropdown
                                options={roleOptions}
                                value={editingUser.role}
                                onChange={(e: ComponentEvent<string>) => setEditingUser({ ...editingUser, role: e.value })}
                                disabled={updating}
                            />
                        </FieldLabel>

                        <div className="flex justify-end gap-3">
                            <Button
                                label="Cancel"
                                variant="default"
                                layout="outlined"
                                onClick={() => setShowEditModal(false)}
                                disabled={updating}
                            />
                            <Button
                                label="Update Role"
                                leftIcon={SaveIcon}
                                variant="primary"
                                onClick={handleUpdateRole}
                                isLoading={updating}
                                disabled={updating}
                            />
                        </div>
                    </div>
                )}
            </Modal>

            {/* Remove User Modal */}
            <Modal
                isOpen={showRemoveModal.show}
                onClose={() => !removing && setShowRemoveModal({ show: false })}
                title="Remove User"
                size="md"
            >
                <div className="space-y-4">
                    <p className="text-slate-600 dark:text-slate-400">Are you sure you want to remove this user from the organization?</p>
                    {showRemoveModal.user && (
                        <div className="p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
                            <div className="font-medium text-slate-900 dark:text-white">{showRemoveModal.user.name}</div>
                            <div className="text-sm text-slate-500 dark:text-slate-400">{showRemoveModal.user.email}</div>
                        </div>
                    )}
                    <p className="text-sm text-red-600 dark:text-red-400">
                        ⚠️ This action cannot be undone. The user will lose access to all organization resources.
                    </p>
                    <div className="flex justify-end gap-3">
                        <Button
                            label="Cancel"
                            variant="default"
                            layout="outlined"
                            onClick={() => setShowRemoveModal({ show: false })}
                            disabled={removing}
                        />
                        <Button label="Remove User" variant="danger" onClick={handleRemove} isLoading={removing} disabled={removing} />
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default UserManagement;
